import { EventDocumentType } from "@ac-expo/api-types";
import {
  useMutation,
  UseMutationOptions,
  useQueryClient,
} from "@tanstack/react-query";
import { isAxiosError } from "axios";
import { privateAxios } from "../axiosInstances";
import { eventDocumentsQueryKeys } from "../query-keys/event-documents.query-keys";

export type CreateEventDocumentBody = {
  structureDocumentIds: string[];
};

export type UpdateEventDocumentOrderBody = {
  order: number;
};

const createEventDocument = async (
  eventId: string,
  structureId: string,
  body: CreateEventDocumentBody
): Promise<EventDocumentType[]> => {
  try {
    const { data } = await privateAxios.post<EventDocumentType[]>(
      `/events/${eventId}/exhibitors/${structureId}/documents`,
      body
    );
    return data;
  } catch (error) {
    if (isAxiosError(error)) {
      error.message = error.response?.data.message || error.message;
    }
    throw error;
  }
};

const deleteEventDocument = async (
  eventId: string,
  structureId: string,
  eventDocumentId: string
): Promise<void> => {
  try {
    await privateAxios.delete(
      `/events/${eventId}/exhibitors/${structureId}/documents/${eventDocumentId}`
    );
  } catch (error) {
    if (isAxiosError(error)) {
      error.message = error.response?.data.message || error.message;
    }
    throw error;
  }
};

const updateEventDocumentOrder = async (
  eventId: string,
  structureId: string,
  eventDocumentId: string,
  body: UpdateEventDocumentOrderBody
): Promise<EventDocumentType> => {
  try {
    const { data } = await privateAxios.patch<EventDocumentType>(
      `/events/${eventId}/exhibitors/${structureId}/documents/${eventDocumentId}/order`,
      body
    );
    return data;
  } catch (error) {
    if (isAxiosError(error)) {
      error.message = error.response?.data.message || error.message;
    }
    throw error;
  }
};

export const useCreateEventDocumentMutation = (
  eventId: string,
  structureId: string,
  options?: UseMutationOptions<
    EventDocumentType[],
    Error,
    CreateEventDocumentBody
  >
) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (body) => createEventDocument(eventId, structureId, body),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: eventDocumentsQueryKeys.list(eventId, structureId),
      });
    },
    ...options,
  });
};

export const useDeleteEventDocumentMutation = (
  eventId: string,
  structureId: string,
  options?: UseMutationOptions<void, Error, string>
) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (eventDocumentId) =>
      deleteEventDocument(eventId, structureId, eventDocumentId),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: eventDocumentsQueryKeys.list(eventId, structureId),
      });
    },
    ...options,
  });
};

export const useUpdateEventDocumentOrderMutation = (
  eventId: string,
  structureId: string,
  options?: UseMutationOptions<
    EventDocumentType,
    Error,
    { eventDocumentId: string; body: UpdateEventDocumentOrderBody }
  >
) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ eventDocumentId, body }) =>
      updateEventDocumentOrder(eventId, structureId, eventDocumentId, body),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: eventDocumentsQueryKeys.list(eventId, structureId),
      });
    },
    ...options,
  });
};
